import { computed, effect, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { Breakpoint, ViewportService } from './viewport.service';

/** Los tres modos del panel lateral (tabla de la §3 de `docs/RESPONSIVE.md`). */
export type SidebarMode = 'overlay' | 'rail' | 'full';

/** Estado de UI compartido: panel lateral y datos del encabezado. */
@Injectable({ providedIn: 'root' })
export class UiService {

  private readonly viewport: ViewportService = inject(ViewportService);

  /**
   * En `rail` / `full` decide el ancho del panel; en `overlay` no pinta nada.
   *
   * Arranca colapsado por debajo de `xl`: en una laptop de 1366px el panel de
   * 270px se lleva el 20% de la pantalla útil. Se decide una sola vez, al
   * construir; a partir de ahí manda el usuario y no se vuelve a tocar solo.
   */
  public readonly sidebarCollapsed: WritableSignal<boolean> = signal<boolean>(this.startsCollapsed());

  private readonly drawerOpen: WritableSignal<boolean> = signal<boolean>(false);

  /** Cajón desplegado. Sólo aplica en `overlay`. */
  public readonly sidebarOpen: Signal<boolean> = this.drawerOpen.asReadonly();

  /**
   * Derivado, sin estado propio: así no hay dos fuentes de verdad peleándose
   * cuando el usuario gira la tableta.
   */
  public readonly sidebarMode: Signal<SidebarMode> = computed<SidebarMode>(() => {
    if (!this.viewport.isDesktop()) {
      return 'overlay';
    }

    return this.sidebarCollapsed() ? 'rail' : 'full';
  });

  /**
   * Datos que el original muestra arriba a la derecha.
   *
   * `surveyCount` es el conteo de **agosto**, que va a la mitad: el corte es el
   * día 11 de 31. Por eso es menor que los meses cerrados de la tendencia, y
   * está bien que lo sea — el tablero se lee al día, no a fin de mes.
   */
  public readonly surveyCount: number = 3148;
  public readonly updatedAt: string = '11/08/2026';
  public readonly period: string = 'Agosto/2026';
  public readonly userName: string = 'Dirección general';

  constructor() {
    // Al salir de `overlay` el cajón deja de tener sentido: sin esto, quien lo
    // abre en vertical y gira a horizontal se queda con un cajón flotando
    // encima de un panel que ya está fijo.
    effect(() => {
      if (this.sidebarMode() !== 'overlay') {
        this.drawerOpen.set(false);
      }
    });
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR API PÚBLICA                            */
  /* -------------------------------------------------------------------------- */

  /** El hamburguesa: abre el cajón en `overlay`, colapsa el panel en el resto. */
  public toggleSidebar(): void {
    if (this.sidebarMode() === 'overlay') {
      this.drawerOpen.update((open: boolean) => !open);

      return;
    }

    this.sidebarCollapsed.update((collapsed: boolean) => !collapsed);
  }

  /** Cierra el cajón de `overlay`: fondo oscurecido, `Escape` y navegación. */
  public closeSidebar(): void {
    this.drawerOpen.set(false);
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR INTERNO                              */
  /* -------------------------------------------------------------------------- */

  /** Ancho inicial por debajo de 1280px, leído del corte vigente. */
  private startsCollapsed(): boolean {
    const initial: Breakpoint = this.viewport.breakpoint();

    return initial !== 'xl' && initial !== 'xxl';
  }
}
